import { useParams } from "react-router-dom";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import {
  SalesInvoiceFindUniqueQuery,
  SalesReferenceDocumentType,
  useSalesInvoiceFindUniqueQuery,
  useSalesOriginalPrintMutation,
} from "../../../generated/sales";
import { IPdfInfo, ISelectOption } from "../../../types/global";
import { formatDate } from "../../../utils/Date";
import { renderAddressString } from "../../../utils/Formatter/Global";
import { Box, CircularProgress, Typography } from "@mui/material";
import SalesPdfLayout from "../../../components/UI/SalesPdf/SalesPdfLayout";
import SalesItemListPdfTable from "../../../components/Table/Pdf/SalesItemList";
import { ISalesItemList } from "../../../types/Sales";
import { usePDFMultiplePage } from "../../../hooks/use-pdf-multiple-page";
import { useReactToPrint } from "react-to-print";
import { ReactInstance, useRef, useState } from "react";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { IContactPerson } from "../../../types/Contact/contact";
import {
  ApprovalTemplateFindByDocumentTypeQuery,
  useApprovalTemplateFindByDocumentTypeQuery,
} from "../../../generated/general";
import { useTranslation } from "react-i18next";
import CustomizedSelect from "../../../components/Custom/CustomizedSelect";
import { useConfirmation } from "../../../hooks/use-confirmation";
import PrintModal from "../../../components/UI/Modal/PrintModal";

type Props = {
  name: string;
  variableName: string;
};

const SalesInvoicePdf = ({ name, variableName }: Props) => {
  const { id } = useParams();
  const componentRef = useRef<ReactInstance | null>(null);
  const graphQLClient = createGraphQLClientWithMiddleware("sales");
  const graphQLClientGeneral = createGraphQLClientWithMiddleware("general");

  const { t } = useTranslation();

  const [printType, setPrintType] = useState<"original" | "copy">("copy");

  const { data, isLoading, refetch } =
    useSalesInvoiceFindUniqueQuery<SalesInvoiceFindUniqueQuery>(graphQLClient, {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    });

  const { data: approvalTemplates, isLoading: isApprovalLoading } =
    useApprovalTemplateFindByDocumentTypeQuery<ApprovalTemplateFindByDocumentTypeQuery>(
      graphQLClientGeneral,
      {
        documentType: "sales_invoice",
      }
    );

  const { mutateAsync } = useSalesOriginalPrintMutation<Error>(graphQLClient);

  const { paginatedItems, startNumbers } = usePDFMultiplePage(
    data?.SalesInvoiceFindUnique, //data
    "sales_invoice", // document type
    141, // name and description row width
    700, // max height without footer
    500, // max height with footer
    226.484, // left header minimum height
    226.484, // right header minimum height
    undefined, // left footer minimum height (you don't need to specify this if the document doesn't have remark and tags)
    undefined // right footer minimum height (you don't need to specify this if the document doesn't have summary)
  );

  const printTypeOption: ISelectOption[] = [
    {
      label: t(`print.original`),
      value: "original",
      disabled:
        data?.SalesInvoiceFindUnique.is_original_print &&
        data?.SalesInvoiceFindUnique.is_original_print[variableName],
    },
    {
      label: t(`print.copy`),
      value: "copy",
    },
  ];

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: data?.SalesInvoiceFindUnique.unique_id,
    onAfterPrint: async () => {
      if (printType === "original") {
        await mutateAsync({
          input: {
            document_id: id ? parseInt(id) : 0,
            document_type: SalesReferenceDocumentType.SalesInvoice,
            key: variableName,
          },
        });
        await refetch();
        setPrintType("copy");
      }
    },
  });

  const taxInvoice = data?.SalesInvoiceFindUnique.tax_invoice_no;

  const highlightSection: IPdfInfo = {
    headers: [
      "เลขที่เอกสาร",
      ...(taxInvoice ? ["เลขที่ใบกำกับภาษี"] : []),
      "วันที่ออกเอกสาร",
      // "ใช้ได้ถึง",
    ],
    info: [
      data?.SalesInvoiceFindUnique?.unique_id || "-",
      ...(taxInvoice ? [taxInvoice] : []),
      data?.SalesInvoiceFindUnique?.issue_date
        ? formatDate(data?.SalesInvoiceFindUnique.issue_date)
        : "-",
      // data?.SalesInvoiceFindUnique?.due_date
      //   ? formatDate(data?.SalesInvoiceFindUnique.due_date)
      //   : "-",
    ],
  };

  const identityNoBranch = data?.SalesInvoiceFindUnique.customer_details
    .identity_no
    ? // &&
      // data?.SalesInvoiceFindUnique.branch
      // ? `${data?.SalesInvoiceFindUnique.customer_details.identity_no} (${data?.SalesInvoiceFindUnique.branch.unique_id})`
      `${data?.SalesInvoiceFindUnique.customer_details.identity_no}`
    : "-";

  const leftSection: IPdfInfo = {
    headers: [
      "ลูกค้า",
      "ที่อยู่",
      "ที่อยู่จัดส่งสินค้า",
      "เลขประจำตัวผู้เสียภาษี",
      "เบอร์โทรศัพท์@โทรสาร",
      "อีเมล",
    ],
    info: [
      `${data?.SalesInvoiceFindUnique.customer_unique_id} ${data?.SalesInvoiceFindUnique.customer_details.name}`,
      renderAddressString(
        data?.SalesInvoiceFindUnique.customer_details.billing_address
      ),
      renderAddressString(
        data?.SalesInvoiceFindUnique.customer_details.delivery_address
      ),
      identityNoBranch,
      `${data?.SalesInvoiceFindUnique.customer_details.phone || "-"}@${
        data?.SalesInvoiceFindUnique.customer_details.fax || "-"
      }`,
      data?.SalesInvoiceFindUnique.customer_details.email || "-",
    ],
  };

  const salesPersonFirstName =
    (data?.SalesInvoiceFindUnique.related_user_list &&
      data?.SalesInvoiceFindUnique.related_user_list.length > 0 &&
      data.SalesInvoiceFindUnique.related_user_list[0].first_name) ||
    "-";

  const salesPersonLastname =
    (data?.SalesInvoiceFindUnique.related_user_list &&
      data?.SalesInvoiceFindUnique.related_user_list.length > 0 &&
      data.SalesInvoiceFindUnique.related_user_list[0].last_name) ||
    "";
  const salePerson = salesPersonFirstName + " " + salesPersonLastname;

  const contactPerson: IContactPerson =
    data?.SalesInvoiceFindUnique.contact_person_list &&
    data?.SalesInvoiceFindUnique.contact_person_list.length > 0 &&
    data?.SalesInvoiceFindUnique.contact_person_list[0];

  const contactPersonName = contactPerson
    ? `${contactPerson.title_name}${contactPerson.first_name} ${contactPerson.last_name}`
    : "-";

  const contactPersonPhone = contactPerson
    ? contactPerson.contact_channel_list.find(
        (channel) => channel.contact_channel_type === "phone"
      )?.contact_channel_info || "-"
    : "-";

  const rightSection: IPdfInfo = {
    headers: [
      "อ้างอิง",
      "พนักงานขาย",
      "ครบกำหนดชำระ",
      "หมายเลขอ้างอิง",
      "ผู้ติดต่อ",
      "เบอร์โทรศัพท์",
    ],
    info: [
      data?.SalesInvoiceFindUnique.reference_document_list
        ?.map((doc) => doc.document_unique_id)
        .join(", ") || "-",
      salePerson || "-",
      `${data?.SalesInvoiceFindUnique.credit_day?.toString()} วัน` || "-",
      data?.SalesInvoiceFindUnique.reference_no || "-",
      contactPersonName,
      contactPersonPhone,
    ],
  };

  const needTaxInvoice = data?.SalesInvoiceFindUnique.need_tax_invoice;

  const needOriginal =
    needTaxInvoice &&
    !["draft", "wait_approve"].includes(
      data?.SalesInvoiceFindUnique.aggrid_status || ""
    );

  const {
    confirmation,
    openConfirmationHandler,
    closeConfirmationHandler,
    submitConfirmationHandler,
  } = useConfirmation(() => handlePrint());

  if (id && (isLoading || isApprovalLoading)) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const allPages = paginatedItems.length;

  return (
    <>
      <Box maxWidth={1650}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5">{name}</Typography>
            <Typography>{`เลขที่เอกสาร ${data?.SalesInvoiceFindUnique.unique_id}`}</Typography>
          </Box>

          <Box display={"flex"} gap={1}>
            {needOriginal && (
              <Box>
                <CustomizedSelect
                  sx={{ width: 120 }}
                  label={t("print.type")}
                  options={printTypeOption}
                  onChange={(e) => {
                    const value = e.target.value as "copy" | "original";
                    setPrintType(value);
                  }}
                  value={printType}
                />
              </Box>
            )}
            <CustomizedButton
              fullWidth
              title="พิมพ์เอกสาร"
              variant="outlined"
              onClick={
                printType === "original" ? openConfirmationHandler : handlePrint
              }
            />
          </Box>
        </Box>
        <Box display={"flex"} justifyContent={"center"}>
          <Box ref={componentRef} sx={{ m: "auto" }}>
            {paginatedItems.map((item, index) => (
              <SalesPdfLayout
                printType={needOriginal ? printType : undefined}
                highlightSection={highlightSection}
                leftSection={leftSection}
                rightSection={rightSection}
                name={`${name}`}
                multiplePage={paginatedItems.length > 1}
                page={index + 1}
                allPages={allPages}
                data={data?.SalesInvoiceFindUnique}
                summary={index === paginatedItems.length - 1}
                salePerson={salePerson}
                totalAmount={
                  data?.SalesInvoiceFindUnique.total_amount
                    ? data?.SalesInvoiceFindUnique.total_amount
                    : 0
                }
                documentType="sales_invoice"
                hideAdditionalDiscount={false}
                showPaymentSection={false} //Change to true to show payment section below footer
                approvalTemplates={
                  approvalTemplates?.ApprovalTemplateFindByDocumentType || []
                }
              >
                <SalesItemListPdfTable
                  itemList={(item as ISalesItemList[]) || []}
                  startNumber={startNumbers[index]}
                  isLastPage={index === paginatedItems.length - 1}
                  lastTable={
                    paginatedItems[paginatedItems.length - 1].length === 0
                      ? index === paginatedItems.length - 2
                      : index === paginatedItems.length - 1
                  }
                  totalAmount={
                    data?.SalesInvoiceFindUnique.total_amount
                      ? data?.SalesInvoiceFindUnique.total_amount
                      : 0
                  }
                  hasItems={item.length > 0}
                />
              </SalesPdfLayout>
            ))}
          </Box>
        </Box>
      </Box>
      <PrintModal
        open={confirmation}
        closeModalHandler={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default SalesInvoicePdf;
